import { useEffect, useRef, useState } from 'react';
import {
  DeviceInfo,
  WifiSavedNetwork,
  WifiSavedOperation,
  deleteSavedWifiNetworks,
  getDeviceInfo,
  requestSavedWifiRefresh,
} from '../../services/admin';
import {
  SAVED_PENDING_WINDOW_MS,
  SAVED_SNAPSHOT_MAX_AGE_MS,
  ageMs,
  ageText,
  deleteWarnings,
  isStale,
  operationText,
  outcomeText,
  savedReasonText,
} from './wifiNetworkText';

// Saved Wi-Fi networks of ONE radio (documentation/protocol.md "Saved Wi-Fi networks"). Shows only
// what the radio itself reported, with its freshness and the radio's own capability verdict; never a
// network inferred from a scan, never a password. Deleting removes exactly the confirmed entries of the
// current snapshot ("Delete all" = every listed entry, not a wildcard), after a confirmation that names
// every SSID and warns when the current or last network would go.

export interface SavedConfirm {
  mode: 'selected' | 'all';
  entries: WifiSavedNetwork[];
}

export interface WifiSavedNetworksViewProps {
  online: boolean;
  capable: boolean;
  saved: DeviceInfo['wifi_saved_networks'];
  lastOperation: WifiSavedOperation | null;
  now: number;
  selectedIds: number[];
  confirm: SavedConfirm | null;
  pending: 'refresh' | 'delete' | null;
  message: string | null;
  onToggle: (networkId: number) => void;
  onRefresh: () => void;
  onDeleteSelected: () => void;
  onDeleteAll: () => void;
  onConfirm: () => void;
  onCancel: () => void;
}

export function WifiSavedNetworksView(p: WifiSavedNetworksViewProps) {
  if (!p.capable) {
    return (
      <p className="text-sm text-gray-400">
        This radio&rsquo;s app version cannot report its saved Wi-Fi networks. Update the radio&rsquo;s app to manage them here.
      </p>
    );
  }
  const saved = p.saved;
  const networks = saved?.status === 'SUPPORTED' ? saved.networks : [];
  const tooOld = !saved || isStale(saved.updated_at, p.now, SAVED_SNAPSHOT_MAX_AGE_MS);
  // The server refuses a new deletion while one younger than five minutes awaits the radio.
  const awaitingRadio = p.lastOperation?.status === 'PENDING' && (ageMs(p.lastOperation.created_at, p.now) ?? 0) < SAVED_PENDING_WINDOW_MS;
  const busy = p.pending !== null || !p.online || p.confirm !== null || awaitingRadio;
  const selected = networks.filter((n) => p.selectedIds.includes(n.network_id));
  const warnings = p.confirm ? deleteWarnings(p.confirm.entries, networks) : null;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={p.onRefresh}
          disabled={p.pending !== null || !p.online}
          className="bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-white text-sm font-medium py-1.5 px-3 rounded"
        >
          {p.pending === 'refresh' ? 'Refreshing…' : 'Refresh list'}
        </button>
        {saved && (
          <p className="text-xs text-gray-400">
            Reported by the radio {ageText(saved.updated_at, p.now)}
            {saved.status === 'SUPPORTED' && tooOld ? ' · refresh before deleting' : ''}
          </p>
        )}
        {p.message && <p className="text-sm text-gray-400">{p.message}</p>}
      </div>

      {!saved && <p className="text-sm text-gray-400">The radio has not reported its saved networks yet.</p>}
      {saved?.status === 'UNSUPPORTED' && (
        <p className="text-sm text-amber-400">
          The radio reports that it cannot list its saved networks: {savedReasonText(saved.reason)}. Saved networks cannot be managed remotely on this radio.
        </p>
      )}
      {saved?.status === 'UNAVAILABLE' && (
        <p className="text-sm text-amber-400">The saved networks are not available right now: {savedReasonText(saved.reason)}.</p>
      )}
      {saved?.status === 'SUPPORTED' && networks.length === 0 && <p className="text-sm text-gray-400">The radio has no saved Wi-Fi networks.</p>}

      {networks.length > 0 && (
        <ul className="space-y-1">
          {networks.map((n) => (
            <li key={n.network_id} className="flex items-center gap-3 border-t border-gray-700 pt-1">
              <input
                type="checkbox"
                aria-label={`Select ${n.ssid}`}
                checked={p.selectedIds.includes(n.network_id)}
                onChange={() => p.onToggle(n.network_id)}
                disabled={busy}
              />
              <span className="text-white text-sm">{n.ssid}</span>
              <span className="text-gray-400 text-xs">{n.security}</span>
              {n.current && <span className="text-xs text-green-400 border border-green-600 rounded px-1">current</span>}
            </li>
          ))}
        </ul>
      )}

      {networks.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={p.onDeleteSelected}
            disabled={busy || tooOld || selected.length === 0}
            className="bg-red-800 hover:bg-red-700 disabled:bg-gray-700 disabled:text-gray-400 text-white text-sm font-medium py-1.5 px-3 rounded"
          >
            Delete selected{selected.length > 0 ? ` (${selected.length})` : ''}
          </button>
          <button
            type="button"
            onClick={p.onDeleteAll}
            disabled={busy || tooOld}
            className="bg-red-900 hover:bg-red-800 disabled:bg-gray-700 disabled:text-gray-400 text-white text-sm font-medium py-1.5 px-3 rounded"
          >
            Delete all ({networks.length})
          </button>
        </div>
      )}

      {p.confirm && warnings && (
        <div role="alertdialog" className="rounded border border-red-700 bg-red-900/20 p-3 space-y-2">
          <p className="font-semibold text-white">
            Delete {p.confirm.entries.length === 1 ? 'this saved network' : `these ${p.confirm.entries.length} saved networks`} from the radio?
          </p>
          <ul className="list-disc list-inside text-sm text-white">
            {p.confirm.entries.map((n) => (
              <li key={n.network_id}><span className="font-mono">{n.ssid}</span> <span className="text-gray-400">({n.security})</span></li>
            ))}
          </ul>
          {warnings.current && (
            <p className="text-sm text-amber-300">
              {warnings.current.ssid} is the network the radio is connected to now. Removing it disconnects the radio from Wi-Fi; it stays reachable only over mobile data.
            </p>
          )}
          {warnings.leavesNone && (
            <p className="text-sm text-amber-300">No saved Wi-Fi network will remain: the radio will not reconnect to any Wi-Fi network by itself.</p>
          )}
          <p className="text-xs text-gray-400">Only the entries above are removed. The radio checks each one again before removing it.</p>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={p.onCancel} className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white text-sm font-semibold rounded">Cancel</button>
            <button type="button" onClick={p.onConfirm} className="px-3 py-1.5 bg-red-800 hover:bg-red-700 text-white text-sm font-semibold rounded">
              Delete {p.confirm.entries.length === 1 ? p.confirm.entries[0].ssid : `${p.confirm.entries.length} networks`}
            </button>
          </div>
        </div>
      )}

      {p.lastOperation && (
        <div className="rounded border border-gray-700 bg-gray-900 p-3 space-y-1">
          <p className="text-xs uppercase font-semibold text-gray-400">
            Last deletion · {new Date(p.lastOperation.created_at).toLocaleString()}
            {p.lastOperation.requested_by_username ? ` · ${p.lastOperation.requested_by_username}` : ''}
          </p>
          <p className="text-sm text-white">{operationText(p.lastOperation, p.now)}</p>
          {p.lastOperation.status !== 'PENDING' && (
            <ul className="text-sm">
              {p.lastOperation.results.map((r) => (
                <li key={r.network_id} className={r.outcome === 'REMOVED' ? 'text-green-400' : 'text-amber-300'}>
                  <span className="font-mono">{r.ssid}</span>: {outcomeText(r.outcome, r.code)}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default function WifiSavedNetworks({ radioId, info, online, onInfo }: {
  radioId: string;
  info: DeviceInfo;
  online: boolean;
  onInfo: (info: DeviceInfo) => void;
}) {
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [confirm, setConfirm] = useState<SavedConfirm | null>(null);
  const [pending, setPending] = useState<'refresh' | 'delete' | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const saved = info.wifi_saved_networks ?? null;
  const networks = saved?.status === 'SUPPORTED' ? saved.networks : [];
  const snapshotId = saved?.snapshot_id;

  // Bumped when the radio changes or the panel goes away: older polls then stop without updating.
  const generation = useRef(0);
  useEffect(() => {
    const counter = generation; // the counter object itself, not a DOM node
    counter.current++;
    return () => { counter.current++; };
  }, [radioId]);

  // A new snapshot invalidates the selection and any open confirmation.
  useEffect(() => {
    setSelectedIds([]);
    setConfirm(null);
  }, [snapshotId]);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(t);
  }, []);

  const poll = (done: (fresh: DeviceInfo) => boolean, timeoutMs: number, onTimeout: string) => {
    const deadline = Date.now() + timeoutMs;
    const gen = generation.current;
    const tick = async () => {
      if (gen !== generation.current) return;
      const fresh = await getDeviceInfo(radioId).catch(() => null);
      if (gen !== generation.current) return;
      if (fresh) {
        onInfo(fresh);
        setNow(Date.now());
        if (done(fresh)) {
          setPending(null);
          return;
        }
      }
      if (Date.now() > deadline) {
        setPending(null);
        setMessage(onTimeout);
        return;
      }
      setTimeout(tick, 3_000);
    };
    setTimeout(tick, 2_000);
  };

  const onRefresh = async () => {
    setPending('refresh');
    setMessage(null);
    try {
      const ticket = await requestSavedWifiRefresh(radioId);
      const since = new Date(ticket.requested_at).getTime();
      poll((fresh) => !!fresh.wifi_saved_networks && new Date(fresh.wifi_saved_networks.updated_at).getTime() >= since,
        30_000, 'The radio did not report its saved networks yet.');
    } catch (e) {
      setPending(null);
      setMessage(e instanceof Error ? e.message : 'Failed to request the saved networks');
    }
  };

  const onConfirm = async () => {
    if (!confirm || !snapshotId) return;
    const c = confirm;
    setConfirm(null);
    setPending('delete');
    setMessage(null);
    try {
      const op = await deleteSavedWifiNetworks(radioId, snapshotId, c.mode, c.entries);
      setSelectedIds([]);
      poll((fresh) => fresh.wifi_saved_last_operation?.id === op.id && fresh.wifi_saved_last_operation.status !== 'PENDING',
        90_000, 'Waiting for the radio. The result is shown here when it arrives.');
    } catch (e) {
      setPending(null);
      setMessage(e instanceof Error ? e.message : 'Failed to request the deletion');
      const gen = generation.current;
      getDeviceInfo(radioId).then((fresh) => { if (gen === generation.current) onInfo(fresh); }).catch(() => undefined);
    }
  };

  return (
    <WifiSavedNetworksView
      online={online}
      capable={!!info.wifi_capabilities?.saved_networks}
      saved={saved}
      lastOperation={info.wifi_saved_last_operation ?? null}
      now={now}
      selectedIds={selectedIds}
      confirm={confirm}
      pending={pending}
      message={message}
      onToggle={(id) => setSelectedIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]))}
      onRefresh={onRefresh}
      onDeleteSelected={() => setConfirm({ mode: 'selected', entries: networks.filter((n) => selectedIds.includes(n.network_id)) })}
      onDeleteAll={() => setConfirm({ mode: 'all', entries: networks })}
      onConfirm={onConfirm}
      onCancel={() => setConfirm(null)}
    />
  );
}
